import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bot,
  Building2,
  Check,
  Copy,
  ExternalLink,
  FolderInput,
  Import,
  LayoutGrid,
  List,
  LogOut,
  CircleOff,
  Monitor,
  Moon,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  Sun,
  Trash2,
  UserX,
} from 'lucide-react';
import { api, ApiError, type Board, type Session, type Team } from '../../lib/api';
import { rememberOrg, useTeamSpaces } from '../../lib/spaces';
import type { BoardView } from '../WorkspaceNav';
import { useNavPlace } from '../navState';
import { favoritesKey } from '../../lib/favorites';
import { sessionKey, useRefreshSession, useSession } from '../../lib/session';
import { useTheme } from '../../lib/theme';
import { useMotion } from '../../lib/motion';
import { relativeTime } from '../../lib/time';
import { Avatar, CenterScreen, Menu, Spinner, toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import { TEMPLATE_IDS } from '../../engine/templates';
import { customTemplateParam } from '../../templates/api';
import { TeamTemplatesGallery } from '../../templates/Gallery';
import { TemplateTabs, useTemplateTab } from '../../templates/Tabs';
import { ConfirmDialog, NameDialog, TemplateArt } from './parts';
import { OrgSettings } from './OrgSettings';
import { DeleteAccountDialog, forgetLocal } from '../auth/Legal';
import { ImportMiroDialog } from './ImportMiro';
import { SearchResults, useDebounced } from './Search';
import { NotificationBell } from '../../notifications/Bell';
import { EmailPrefsMenu, TelegramPrefsMenu } from '../../notifications/EmailPrefs';
import { ApiTokensDialog } from './ApiTokens';
import './dashboard.css';
import { MobileBar } from '../MobileBar';
import { openCreate } from '../spaces/dialogs';
import { ProjectCards } from '../spaces/ProjectCards';
import { useProjects, type TrackerProject } from '../tracker/model';
import { recentProjects } from '../../lib/recent';
import { NewsStrip } from '../../docs/News';

type View = BoardView;

export function Dashboard() {
  const session = useSession();
  if (session.isLoading) {
    return (
      <CenterScreen>
        <Spinner />
      </CenterScreen>
    );
  }
  if (!session.data?.user) return <Navigate to={`/login${location.pathname !== '/' ? `?next=${encodeURIComponent(location.pathname)}` : ''}`} replace />;
  return <DashboardInner session={session.data} />;
}

function DashboardInner({ session }: { session: Session }) {
  const { t } = useTranslation();
  const user = session.user!;
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org');
  const org = session.orgs.find((o) => o.id === orgId) ?? null;
  const view = (params.get('view') as View) || 'all';
  const teamId = params.get('team');
  // Links from before spaces: a board folder is a space now.
  const legacyProject = params.get('project');
  const [q, setQ] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  // Back from signing in to Miro (crates/server/src/api/import_miro/oauth.rs).
  const [miroConnection, setMiroConnection] = useState<string | null>(null);
  useEffect(() => {
    const conn = params.get('importMiro');
    const failed = params.get('importMiroError');
    // "Import from Miro" in the organization's menu.
    const asked = params.get('import') === 'miro';
    if (!conn && !failed && !asked) return;
    if (conn) setMiroConnection(conn);
    if (conn || asked) setImportOpen(true);
    if (failed) toast(errorText(failed));
    const next = new URLSearchParams(params);
    next.delete('importMiro');
    next.delete('importMiroError');
    next.delete('import');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tplTab, setTplTab] = useTemplateTab();
  // The sidebar of other pages opens where the person last was.
  useEffect(() => rememberOrg(org?.id ?? null), [org?.id]);

  const teams = session.teams.filter((tm) => (org ? tm.orgId === org.id : tm.orgId === null));
  const team = teams.find((tm) => tm.id === teamId) ?? null;
  const defaultTeam = team ?? teams[0] ?? session.teams[0];

  const filter = view === 'org' ? 'all' : view;
  // "Everything in the organization" with its filters; "Starred and recent" has a page of its own.
  const everything = view === 'all' || view === 'mine' || view === 'shared' || view === 'projects';
  const ownPage = view === 'recent' || view === 'starred' || view === 'projects';
  // Outside the trash, typing searches titles and board content (GET /api/search).
  const searchQ = useDebounced(q.trim(), 200);
  const searching = view !== 'trash' && view !== 'org' && q.trim().length > 0;
  const boards = useQuery({
    queryKey: ['boards', { filter, team: teamId, org: org?.id, q }],
    queryFn: () => {
      const s = new URLSearchParams({ filter });
      if (teamId) s.set('team', teamId);
      if (org && !teamId) s.set('org', org.id);
      if (q.trim()) s.set('q', q.trim());
      return api.get<Board[]>(`/boards?${s}`);
    },
    enabled: view !== 'org' && !ownPage && !searching,
    placeholderData: (prev) => prev,
  });

  /** `template`: a built-in id or `team:{id}` (see templates/api.ts); the board starts empty and fills itself. */
  const createBoard = async (template?: string, title?: string) => {
    try {
      const board = await api.post<Board>('/boards', { teamId: defaultTeam?.id, title });
      void qc.invalidateQueries({ queryKey: ['boards'] });
      navigate(`/b/${board.id}${template ? `?template=${encodeURIComponent(template)}` : ''}`);
    } catch (e) {
      toast(errorText(e instanceof ApiError ? e.code : 'internal'));
    }
  };

  const title = useMemo(() => {
    if (view === 'org') return org?.name ?? '';
    if (team) return team.personal ? t('nav.personal') : team.name;
    if (everything) return t(org ? 'nav.everything' : 'nav.everythingPersonal');
    if (view === 'recent' || view === 'starred') return t('nav.starredRecent');
    return t(`nav.${view}`);
  }, [view, org, team, everything, t]);

  useNavPlace(org?.id ?? null, team ? { kind: 'none' } : { kind: 'view', view });

  if (legacyProject) return <Navigate to={`/s/${legacyProject}`} replace />;

  return (
    <>
      <main className="main">
        <MobileBar />
        {view === 'org' && org ? (
          <OrgSettings org={org} session={session} />
        ) : (
          <>
            <div className="main-head">
              <h1>{title}</h1>
              <div className="search-field search">
                <Search size={16} />
                <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={view === 'trash' ? t('dashboard.searchBoards') : t('search.placeholder')} />
              </div>
              <NotificationBell />
              {view !== 'trash' && view !== 'projects' && (
                <button className="btn" onClick={() => setImportOpen(true)}>
                  <Import size={17} /> {t('importMiro.open')}
                </button>
              )}
              {view === 'projects' ? (
                <button className="btn primary" onClick={() => openCreate('project', { org: org?.id ?? null })}>
                  <Plus size={17} /> {t('tracker.newProject')}
                </button>
              ) : (
                view !== 'trash' && (
                  <button className="btn primary" onClick={() => createBoard()}>
                    <Plus size={17} /> {t('dashboard.newBoard')}
                  </button>
                )
              )}
            </div>
            {everything && !team && (
              <div className="dash-chips" role="tablist">
                {(['all', 'mine', 'shared', 'projects'] as const).map((v) => (
                  <button
                    key={v}
                    role="tab"
                    className="chip"
                    data-active={view === v}
                    aria-selected={view === v}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      if (v === 'all') next.delete('view');
                      else next.set('view', v);
                      setParams(next, { replace: true });
                    }}
                  >
                    {t(`dashboard.chip.${v}`)}
                  </button>
                ))}
              </div>
            )}
            {!user.emailVerified && <VerifyBanner email={user.email} />}
            <DiscoverOrgs />
            {view === 'all' && !q && (
              <>
                <div className="section-head">
                  <h3>{t('dashboard.startWith')}</h3>
                  <TemplateTabs tab={tplTab} onChange={setTplTab} />
                </div>
                {tplTab === 'team' && <TeamTemplatesGallery onUse={(tpl) => void createBoard(customTemplateParam(tpl.id), tpl.name)} />}
                <div className="templates" style={tplTab === 'team' ? { display: 'none' } : undefined}>
                  {(['blank', ...TEMPLATE_IDS] as const).map((id) => (
                    <button key={id} className="tpl" onClick={() => createBoard(id === 'blank' ? undefined : id)}>
                      <TemplateArt id={id} />
                      <span className="tpl-name">{t(`templates.${id}`)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {searching ? (
              <SearchResults q={searchQ || q.trim()} org={org?.id ?? null} team={teamId} project={null} />
            ) : view === 'projects' ? (
              <OrgProjects org={org?.id ?? null} />
            ) : view === 'recent' || view === 'starred' ? (
              <StarredRecent org={org?.id ?? null} teams={session.teams} />
            ) : (
              <>
                {view === 'all' && !q && <NewsStrip />}
                <BoardsSection boards={boards.data} loading={boards.isLoading} trash={view === 'trash'} searching={!!q} onCreate={() => createBoard()} teams={session.teams} />
              </>
            )}
          </>
        )}
      </main>
      <ImportMiroDialog
        open={importOpen}
        onOpenChange={(v) => {
          setImportOpen(v);
          if (!v) setMiroConnection(null);
        }}
        session={session}
        defaultTeamId={defaultTeam?.id}
        connection={miroConnection}
      />
    </>
  );
}

/** Projects of the organization in view (or the personal ones), as on a space's page. */
function OrgProjects({ org }: { org: string | null }) {
  const projects = useProjects();
  const list = (projects.data ?? []).filter((p) => (org ? p.orgId === org : p.orgId === null)).sort((a, b) => a.key.localeCompare(b.key));
  return <ProjectCards projects={projects.data ? list : undefined} loading={projects.isLoading} onCreate={() => openCreate('project', { org })} />;
}

/** "Starred and recent": starred boards, then boards and projects opened lately. */
function StarredRecent({ org, teams }: { org: string | null; teams: Team[] }) {
  const { t } = useTranslation();
  const scope = (filter: string) => {
    const s = new URLSearchParams({ filter });
    if (org) s.set('org', org);
    return `/boards?${s}`;
  };
  const starred = useQuery({ queryKey: ['boards', { filter: 'starred', org }], queryFn: () => api.get<Board[]>(scope('starred')) });
  const recent = useQuery({ queryKey: ['boards', { filter: 'recent', org }], queryFn: () => api.get<Board[]>(scope('recent')) });
  const projects = useProjects();
  const recentIds = useMemo(() => recentProjects().map((r) => r.id), []);
  const lately = recentIds.map((id) => projects.data?.find((p) => p.id === id && !p.archived && (org ? p.orgId === org : true))).filter((p): p is TrackerProject => !!p);
  return (
    <>
      {lately.length > 0 && (
        <section className="dash-recent">
          <div className="section-head">
            <h3>{t('nav.recentProjects')}</h3>
          </div>
          <ProjectCards projects={lately} loading={false} />
        </section>
      )}
      <section className="dash-recent">
        {starred.data && starred.data.length === 0 ? (
          <>
            <div className="section-head">
              <h3>{t('nav.starred')}</h3>
            </div>
            <p className="muted">{t('dashboard.noStarred')}</p>
          </>
        ) : (
          <BoardsSection boards={starred.data} loading={starred.isLoading} trash={false} searching={false} teams={teams} heading={t('nav.starred')} />
        )}
      </section>
      <section className="dash-recent">
        <BoardsSection boards={recent.data} loading={recent.isLoading} trash={false} searching={false} teams={teams} heading={t('nav.recentBoards')} />
      </section>
    </>
  );
}

function VerifyBanner({ email }: { email: string }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  return (
    <div className="banner">
      {t('auth.verifyBanner', { email })}
      <button
        className="btn sm"
        disabled={sent}
        onClick={async () => {
          await api.post('/auth/resend-verification');
          setSent(true);
          toast(t('auth.resent'));
        }}
      >
        {sent ? <Check size={15} /> : null} {t('auth.resend')}
      </button>
    </div>
  );
}

function DiscoverOrgs() {
  const { t } = useTranslation();
  const refresh = useRefreshSession();
  const discover = useQuery({ queryKey: ['discover'], queryFn: () => api.get<{ id: string; name: string; members: number }[]>('/orgs/discover') });
  if (!discover.data?.length) return null;
  return (
    <div className="banner">
      <Building2 size={18} />
      {t('dashboard.joinOrgs')}: {discover.data.map((o) => o.name).join(', ')}
      <button
        className="btn sm"
        onClick={async () => {
          for (const o of discover.data!) await api.post(`/orgs/${o.id}/join`);
          await refresh();
          void discover.refetch();
        }}
      >
        {t('dashboard.joinOrg')}
      </button>
    </div>
  );
}

/** The person's menu: the menu's footer, or just the avatar on the strip of a hidden menu (`compact`). */
export function UserMenu({ session, compact = false }: { session: Session; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const user = session.user!;
  const { pref, setPref } = useTheme();
  const motion = useMotion();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tokensOpen, setTokensOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const setLang = async (lng: 'ru' | 'en') => {
    await i18n.changeLanguage(lng);
    await api.patch('/me', { locale: lng });
    void qc.invalidateQueries({ queryKey: sessionKey });
  };
  return (
    <Menu.Root>
      <ApiTokensDialog open={tokensOpen} onOpenChange={setTokensOpen} />
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
      <Menu.Trigger asChild>
        {compact ? (
          <button className="icon-btn nav-rail-avatar" aria-label={user.name} title={`${user.name}\n${user.email}`}>
            <Avatar name={user.name} color={user.color} size={28} />
          </button>
        ) : (
          <button className="ws-switch">
            <Avatar name={user.name} color={user.color} size={30} />
            <span style={{ minWidth: 0, flex: 1 }}>
              <div className="ws-name" style={{ fontSize: 'var(--fs-3)' }}>
                {user.name}
              </div>
              <div className="ws-sub" title={user.email}>
                {user.email}
              </div>
            </span>
            <MoreHorizontal size={16} color="var(--text-3)" />
          </button>
        )}
      </Menu.Trigger>
      <Menu.Content side="top" align="start">
        <Menu.Label>{t('nav.theme')}</Menu.Label>
        <Menu.Item icon={<Sun size={16} />} onSelect={() => setPref('light')} keys={pref === 'light' ? ['✓'] : undefined}>
          {t('nav.light')}
        </Menu.Item>
        <Menu.Item icon={<Moon size={16} />} onSelect={() => setPref('dark')} keys={pref === 'dark' ? ['✓'] : undefined}>
          {t('nav.dark')}
        </Menu.Item>
        <Menu.Item icon={<Monitor size={16} />} onSelect={() => setPref('system')} keys={pref === 'system' ? ['✓'] : undefined}>
          {t('nav.system')}
        </Menu.Item>
        <Menu.Separator />
        <Menu.Label>{t('board.motion')}</Menu.Label>
        <Menu.Item icon={<Sparkles size={16} />} onSelect={() => motion.setPref('on')} keys={motion.pref === 'on' ? ['✓'] : undefined}>
          {t('board.motionOn')}
        </Menu.Item>
        <Menu.Item icon={<CircleOff size={16} />} onSelect={() => motion.setPref('off')} keys={motion.pref === 'off' ? ['✓'] : undefined}>
          {t('board.motionOff')}
        </Menu.Item>
        <Menu.Item icon={<Monitor size={16} />} onSelect={() => motion.setPref('system')} keys={motion.pref === 'system' ? ['✓'] : undefined}>
          {t('board.motionSystem')}
        </Menu.Item>
        <Menu.Separator />
        <Menu.Label>{t('nav.language')}</Menu.Label>
        <Menu.Item onSelect={() => setLang('ru')} keys={i18n.language === 'ru' ? ['✓'] : undefined}>
          Русский
        </Menu.Item>
        <Menu.Item onSelect={() => setLang('en')} keys={i18n.language === 'en' ? ['✓'] : undefined}>
          English
        </Menu.Item>
        <Menu.Separator />
        <EmailPrefsMenu />
        <TelegramPrefsMenu />
        <Menu.Separator />
        <Menu.Item icon={<Bot size={16} />} onSelect={() => setTokensOpen(true)}>
          {t('tokens.menu')}
        </Menu.Item>
        <Menu.Item
          icon={<LogOut size={16} />}
          onSelect={async () => {
            await api.post('/auth/logout');
            forgetLocal(qc);
            navigate('/login');
          }}
        >
          {t('auth.logout')}
        </Menu.Item>
        <Menu.Separator />
        <Menu.Item icon={<UserX size={16} />} danger onSelect={() => setDeleteOpen(true)}>
          {t('legal.deleteMenu')}
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

export function BoardsSection({
  boards,
  loading,
  trash,
  searching,
  onCreate,
  teams,
  heading,
}: {
  boards: Board[] | undefined;
  loading: boolean;
  trash: boolean;
  searching: boolean;
  /** Instead of "12 boards" over them. */
  heading?: string;
  /** None: the person cannot create here (a space they only view). */
  onCreate?: () => void;
  teams: Team[];
}) {
  const { t } = useTranslation();
  const [layout, setLayout] = useState<'grid' | 'list'>(() => (localStorage.getItem('vatman.layout') as 'grid' | 'list') || 'grid');
  const [sort, setSort] = useState<'updated' | 'name'>('updated');
  const sorted = useMemo(() => {
    const list = [...(boards ?? [])];
    if (sort === 'name') list.sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [boards, sort]);

  if (loading && !boards) {
    // Placeholders in the shape of board cards, so the page does not jump when boards arrive.
    return (
      <div className="boards" aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="card skeleton">
            <div className="card-thumb skeleton-fill" />
            <div className="card-body">
              <span className="skeleton-line skeleton-fill" />
              <span className="skeleton-line short skeleton-fill" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (sorted.length === 0) {
    return (
      <div className="empty-state">
        <h3>{searching ? t('dashboard.noResults') : trash ? t('dashboard.emptyTrash') : t('dashboard.empty')}</h3>
        {!searching && (trash || onCreate) && <div>{trash ? t('dashboard.trashHint') : t('dashboard.emptyHint')}</div>}
        {!searching && !trash && onCreate && (
          <button className="btn primary" onClick={onCreate}>
            <Plus size={17} /> {t('dashboard.newBoard')}
          </button>
        )}
      </div>
    );
  }
  const setL = (l: 'grid' | 'list') => {
    setLayout(l);
    localStorage.setItem('vatman.layout', l);
  };
  return (
    <>
      <div className="section-head">
        <h3>{heading ? `${heading} · ${sorted.length}` : t('dashboard.boardsCount', { count: sorted.length })}</h3>
        <div className="segmented">
          <button data-active={sort === 'updated'} onClick={() => setSort('updated')}>
            {t('dashboard.sortUpdated')}
          </button>
          <button data-active={sort === 'name'} onClick={() => setSort('name')}>
            {t('dashboard.sortName')}
          </button>
        </div>
        <div className="segmented">
          <button data-active={layout === 'grid'} onClick={() => setL('grid')} aria-label={t('dashboard.grid')}>
            <LayoutGrid size={15} />
          </button>
          <button data-active={layout === 'list'} onClick={() => setL('list')} aria-label={t('dashboard.list')}>
            <List size={15} />
          </button>
        </div>
      </div>
      {layout === 'grid' ? (
        <div className="boards">
          {sorted.map((b) => (
            <BoardCard key={b.id} board={b} trash={trash} teams={teams} />
          ))}
        </div>
      ) : (
        <div className="board-list">
          {sorted.map((b) => (
            <BoardRow key={b.id} board={b} trash={trash} teams={teams} />
          ))}
        </div>
      )}
    </>
  );
}

function useBoardActions(board: Board) {
  const qc = useQueryClient();
  // Space pages and the sidebar count boards too.
  const refreshBoards = async () => {
    // A board's star is a favorite: the menu's list follows.
    await Promise.all([qc.invalidateQueries({ queryKey: ['boards'] }), qc.invalidateQueries({ queryKey: ['spaces'] }), qc.invalidateQueries({ queryKey: favoritesKey })]);
  };
  const onError = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));
  return {
    star: useMutation({ mutationFn: () => (board.starred ? api.del(`/boards/${board.id}/star`) : api.put(`/boards/${board.id}/star`)), onSuccess: refreshBoards, onError }),
    rename: (title: string) => api.patch(`/boards/${board.id}`, { title }).then(refreshBoards).catch(onError),
    duplicate: () => api.post(`/boards/${board.id}/duplicate`).then(refreshBoards).catch(onError),
    trash: () => api.del(`/boards/${board.id}`).then(refreshBoards).catch(onError),
    restore: () => api.post(`/boards/${board.id}/restore`).then(refreshBoards).catch(onError),
    purge: () => api.del(`/boards/${board.id}/purge`).then(refreshBoards).catch(onError),
    move: (spaceId: string) =>
      api
        .patch(`/boards/${board.id}`, { spaceId })
        .then(refreshBoards)
        .catch(onError),
  };
}

function BoardMenu({ board, trash, onOpenChange }: { board: Board; trash: boolean; onOpenChange?: (v: boolean) => void; teams: Team[] }) {
  const { t } = useTranslation();
  const a = useBoardActions(board);
  const [rename, setRename] = useState(false);
  const [purge, setPurge] = useState(false);
  const canEdit = board.role === 'owner' || board.role === 'editor';
  const [moving, setMoving] = useState(false);
  // Moving changes who sees the board: its owner decides (the space's admins are owners too).
  // Loaded when the menu opens: a grid of boards should not ask for every team's spaces.
  const owner = board.role === 'owner';
  const spaces = useTeamSpaces(owner && moving ? board.teamId : null);
  // Where it may go: spaces the person creates in, and where it is now.
  const targets = (spaces.data ?? []).filter((sp) => sp.id === board.spaceId || sp.role === 'admin' || sp.role === 'editor');
  return (
    <>
      <Menu.Root
        onOpenChange={(v) => {
          if (v) setMoving(true);
          onOpenChange?.(v);
        }}
      >
        <Menu.Trigger asChild>
          <button className="icon-btn sm" onClick={(e) => e.stopPropagation()} aria-label={t('common.more')}>
            <MoreHorizontal size={17} />
          </button>
        </Menu.Trigger>
        <Menu.Content align="end">
          {trash ? (
            <>
              <Menu.Item icon={<RotateCcw size={16} />} onSelect={a.restore}>
                {t('dashboard.restore')}
              </Menu.Item>
              <Menu.Item icon={<Trash2 size={16} />} danger onSelect={() => setPurge(true)}>
                {t('dashboard.purge')}
              </Menu.Item>
            </>
          ) : (
            <>
              <Menu.Item icon={<ExternalLink size={16} />} onSelect={() => window.open(`/b/${board.id}`, '_blank')}>
                {t('common.open')}
              </Menu.Item>
              <Menu.Item icon={<Star size={16} />} onSelect={() => a.star.mutate()}>
                {board.starred ? t('dashboard.unstar') : t('dashboard.star')}
              </Menu.Item>
              {canEdit && (
                <Menu.Item icon={<Pencil size={16} />} onSelect={() => setRename(true)}>
                  {t('common.rename')}
                </Menu.Item>
              )}
              <Menu.Item icon={<Copy size={16} />} onSelect={a.duplicate}>
                {t('dashboard.duplicate')}
              </Menu.Item>
              {owner && targets.length > 1 && (
                <Menu.Sub>
                  <Menu.SubTrigger icon={<FolderInput size={16} />}>{t('spaces.moveTo')}</Menu.SubTrigger>
                  <Menu.SubContent>
                    {targets.map((sp) => (
                      <Menu.Item key={sp.id} onSelect={() => a.move(sp.id)} keys={board.spaceId === sp.id ? ['✓'] : undefined}>
                        {sp.icon ? `${sp.icon} ` : ''}
                        {sp.name}
                      </Menu.Item>
                    ))}
                  </Menu.SubContent>
                </Menu.Sub>
              )}
              {board.role === 'owner' && (
                <>
                  <Menu.Separator />
                  <Menu.Item icon={<Trash2 size={16} />} danger onSelect={a.trash}>
                    {t('dashboard.toTrash')}
                  </Menu.Item>
                </>
              )}
            </>
          )}
        </Menu.Content>
      </Menu.Root>
      <NameDialog open={rename} onOpenChange={setRename} title={t('common.rename')} label={t('auth.name')} initial={board.title} onSubmit={a.rename} />
      <ConfirmDialog open={purge} onOpenChange={setPurge} title={t('dashboard.purge')} text={t('dashboard.purgeConfirm')} confirmLabel={t('common.delete')} onConfirm={a.purge} />
    </>
  );
}

function Thumb({ board }: { board: Board }) {
  const [failed, setFailed] = useState(false);
  if (!board.thumbnail || failed) return null;
  return <img src={board.thumbnail} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

function BoardCard({ board, trash, teams }: { board: Board; trash: boolean; teams: Team[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const a = useBoardActions(board);
  const open = () => !trash && navigate(`/b/${board.id}`);
  return (
    <div className="card" onClick={open} role="link" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && open()}>
      <div className="card-thumb">
        <Thumb board={board} />
      </div>
      {board.starred && (
        <span className="card-star-badge">
          <Star size={16} fill="currentColor" />
        </span>
      )}
      <div className="card-actions" data-open={menuOpen} onClick={(e) => e.stopPropagation()}>
        {!trash && (
          <button className="icon-btn sm" onClick={() => a.star.mutate()} aria-label={board.starred ? t('dashboard.unstar') : t('dashboard.star')}>
            <Star size={16} fill={board.starred ? '#F5A524' : 'none'} color={board.starred ? '#F5A524' : 'currentColor'} />
          </button>
        )}
        <BoardMenu board={board} trash={trash} onOpenChange={setMenuOpen} teams={teams} />
      </div>
      <div className="card-body">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="card-title">{board.title}</div>
          <div className="card-meta">
            {t('dashboard.edited', { time: relativeTime(board.updatedAt) })}
            {board.teamName && !trash ? ` · ${board.teamName}` : ''}
          </div>
        </div>
        {board.owner && <Avatar name={board.owner.name} color={board.owner.color} size={24} title={t('dashboard.owner', { name: board.owner.name })} />}
      </div>
    </div>
  );
}

function BoardRow({ board, trash, teams }: { board: Board; trash: boolean; teams: Team[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="board-row" onClick={() => !trash && navigate(`/b/${board.id}`)}>
      <div className="mini">
        <Thumb board={board} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="card-title">
          {board.starred && <Star size={13} fill="#F5A524" color="#F5A524" style={{ marginRight: 6, verticalAlign: -1 }} />}
          {board.title}
        </div>
        <div className="card-meta">{board.teamName}</div>
      </div>
      <div className="card-meta hide-sm">{board.owner?.name}</div>
      <div className="card-meta hide-sm">{relativeTime(board.updatedAt)}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
        <BoardMenu board={board} trash={trash} teams={teams} />
      </div>
      <span className="sr-only">{t('common.open')}</span>
    </div>
  );
}
